'use client'
import { type FC, memo, useRef, useState } from 'react'
import styled from 'styled-components'
import { motion, useInView } from 'motion/react'
import { designGrid, mq, presets, rgbAlpha } from '@/styles'
import { listItemVariants, listVariants } from '@/utils'
import type { TypeCmsContentHeader, TypeCmsContentMedium } from '@/schema'
import { Img, Video } from '../commons'

type Props = {
  data: TypeCmsContentHeader['data']
}

const options = {
  autoPlay: true,
  muted: true,
  loop: true
}

export const getMediaPreview = (medium: TypeCmsContentMedium, playVideo, setPlayVideo) => {
  if (medium?.mediumType === 'image_and_video_files') {
    return (<>
      <Img media={medium.image} className='media cover' />
      <Video className='media' data={medium.video} play={playVideo} setVideoReady={setPlayVideo} options={options}/>
    </>)
  }
  if (medium?.mediumType === 'image_file' && medium.image) return <Img media={medium.image} className='media' />
  if (medium?.mediumType === 'video_file' && medium.video) return <Video className='media' data={medium.video} play={playVideo} setVideoReady={setPlayVideo} options={options} />
  return <div className='media placeholder'></div>
}

export const ContentHeader:FC<Props> = memo(({ data }) => {
  const { title, subtitle, medium } = data
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })
  const [playVideo, setPlayVideo] = useState(true)

  return (
    <Section ref={ref} $playVideo={playVideo}>
      <div className='media-wrapper'>
        {getMediaPreview(medium, playVideo,setPlayVideo)}
      </div>
      <motion.hgroup variants={listVariants} initial='initial' animate={isInView ? 'animate' : 'initial'}>
        <motion.h1 variants={listItemVariants}>{title}</motion.h1>
        <motion.p variants={listItemVariants}>{subtitle}</motion.p>
      </motion.hgroup>
    </Section>
  )
})

const Section = styled.section<{ $playVideo: boolean }>`
  ${designGrid({})}
  margin-bottom: 50px;
  padding-top: 50px;
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('tablet')} {
    margin-bottom: 70px;
    padding-top: 70px;
  }

  ${mq.greaterThan('tablet')} {
    margin-bottom: 140px;
  }

  hgroup {
    align-items: flex-start;
    color: var(--color-white);
    display: flex;
    flex-direction: column;
    gap: 20px;
    grid-column: 1 / span 6;
    grid-row: 1 / span 1;
    margin-top: auto;
    padding: 40px 20px;
    z-index: 2;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 8;
      padding: 40px 30px;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    ${presets.font13_500()}
    text-align: center;
    text-transform: uppercase;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      text-align: left;
    }
  }

  p {
    ${presets.font50()}
    text-align: center;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      text-align: left;
    }
  }

  .media-wrapper {
    aspect-ratio: 16 / 9;
    background-color: ${rgbAlpha('--color-black', .1)};
    border-radius: 20px;
    grid-column: 1 / span 6;
    grid-row: 1 / span 1;
    position: relative;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
    }

    img,
    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 20px;
    }

    img {
      &.cover {
        opacity: ${({ $playVideo }) => $playVideo ? 0 : 1 };
        position: absolute;
        height: 100%;
        object-fit: cover;
        transition: 300ms opacity linear;
        width: 100%;
      }
    }
  }
`
